import { isRetailProfile } from '@/lib/profile/verticalMatchers';
import { isVerificationStatusVerified } from '@/lib/verification/verificationStatus';
import {
  resolveBusinessEntity,
  getBusinessProofRequirements,
} from '@/lib/verification/verificationRequirements';
import type { ProfileData } from '@/types/profile';

/**
 * Trading gate for Shopaholic (retail) sellers.
 *
 * ## Why this exists
 *
 * A Shopaholic seller reaches money through two doors: PayFast at checkout, and
 * WhatsApp — which is the *default* order channel when PayFast is not connected.
 * Until now neither door checked anything about the seller. A fraudster could
 * skip PayFast entirely, list products, and take buyers' money over WhatsApp
 * with no verified identity and no proof a business exists.
 *
 * The rule the owner set (2026-10-21): **a store may not take money — WhatsApp
 * or PayFast — until its ID document is approved and one business proof is
 * verified.** Business proof is whatever the Verification Centre resolves for
 * the seller's declared entity: bank or business-account proof for a sole
 * proprietor, CIPC (or SARS tax compliance) for a registered company.
 *
 * ## Callers must invoke this at the moment money actually moves
 *
 * Not only when a setting is toggled on — that is the bug that left Stay's gate
 * unenforced for so long (`stayPaymentGate.ts` tells the whole story). So this
 * is enforced in three places, not one:
 *
 *   - `POST /api/owner/payments/payfast` — the moment PayFast is connected
 *   - `POST /api/retail/payfast/checkout` — the moment a buyer pays
 *   - `POST /api/retail/whatsapp/order`  — the moment a buyer hands off to
 *     WhatsApp (the number leaks from several surfaces, so the storefront
 *     renders differently too; see the shop page and `RetailGoLiveBadge`)
 *
 * ## No grandfathering
 *
 * The Shopaholic vertical is new; there is no population of trading sellers to
 * protect. Same decision as the restaurant gate — a grandfathering branch would
 * be code that exists for no one and silently disables the gate for whoever
 * matches it next.
 */

export type RetailGateRequirementKey = 'id' | 'business_proof';

export type RetailGateRequirement = {
  key: RetailGateRequirementKey;
  label: string;
  satisfied: boolean;
};

export type RetailTradingGate = {
  /** True when this seller is allowed to take money. */
  open: boolean;
  /** False when the gate does not apply — every non-retail profile passes through. */
  applies: boolean;
  requirements: RetailGateRequirement[];
  missing: RetailGateRequirement[];
  /** Seller-facing explanation, safe to show in a dashboard or an error payload. */
  reason: string;
};

const NOT_APPLICABLE: RetailTradingGate = {
  open: true,
  applies: false,
  requirements: [],
  missing: [],
  reason: '',
};

/**
 * Buyer-facing copy for when the gate is closed. Neutral and non-accusatory:
 * an honest seller still uploading documents is not a fraudster, and a shopper
 * is simply told the shop is not open yet.
 */
export const RETAIL_TRADING_CLOSED_BUYER_ERROR =
  'This shop is still being set up on Zamzi Connect and cannot take orders yet. Please check back soon.';

/**
 * Business proof, resolved from the seller's own entity declaration — the same
 * resolution the Verification Centre and the Stay go-live gate use.
 *
 * A sole proprietor cannot produce CIPC papers, so the accepted document is
 * bank or business-account proof. A registered company proves itself with CIPC
 * or SARS tax compliance. A seller who never answered the sole-prop/registered
 * question is credited with either document, so an upload made before
 * declaring is never held against them.
 */
function isBusinessProofSatisfied(profile: ProfileData): boolean {
  const entity = resolveBusinessEntity(profile);
  const candidates = getBusinessProofRequirements(entity);

  return candidates.some((requirement) => {
    if (!requirement.statusField) return false;
    return isVerificationStatusVerified(profile[requirement.statusField]);
  });
}

/**
 * Whether a Shopaholic seller may take money, by any order route.
 *
 * Callers must invoke this at the moment money actually moves — at the buyer's
 * checkout, at the WhatsApp hand-off, and when PayFast is connected — not only
 * when a setting is turned on.
 */
export const evaluateRetailTradingGate = (
  profile: ProfileData | null | undefined,
): RetailTradingGate => {
  if (!profile || !isRetailProfile(profile)) {
    return NOT_APPLICABLE;
  }

  /**
   * HURU is read as a legacy alias for identity, for the same reason it is in
   * the Stay and restaurant gates: an ID submission used to mirror its result
   * into `huru_clearance_status`, and a seller verified before
   * `id_document_status` existed would otherwise be told they had no identity.
   */
  const identitySatisfied =
    isVerificationStatusVerified(profile.id_document_status) ||
    isVerificationStatusVerified(profile.huru_clearance_status);

  const businessProofSatisfied = isBusinessProofSatisfied(profile);

  const requirements: RetailGateRequirement[] = [
    { key: 'id', label: 'ID verification', satisfied: identitySatisfied },
    { key: 'business_proof', label: 'Business proof', satisfied: businessProofSatisfied },
  ];

  const missing = requirements.filter((requirement) => !requirement.satisfied);

  return {
    open: missing.length === 0,
    applies: true,
    requirements,
    missing,
    reason:
      missing.length === 0
        ? ''
        : `Shopaholic seller verification incomplete: ${missing
            .map((requirement) => requirement.label)
            .join(', ')}. Finish these in the Verification Centre before taking orders.`,
  };
};
